import React, { useMemo } from 'react';
import { AiOutlinePrinter } from 'react-icons/ai';
import { BsClipboard, BsEnvelope, BsFilePlus, BsInbox, BsMailboxFlag, BsPencilSquare, BsSubtract } from 'react-icons/bs';
import { FaRegCalendarPlus } from 'react-icons/fa';
import { IoTrashOutline } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { DrawerBody, DrawerCloseButton } from '@chakra-ui/react';

import { FeatureFlags } from '../../../../../../../../../../components/Authorizer';
import useCompanyFeatures from '../../../../../../../../../../components/Authorizer/useCompanyFeatures';
import CertificateCreationFlow from '../../../../../../../../../../components/CertificateCreationFlow';
import { getMinimumInsuranceRequirements } from '../../../../../../../../../../components/CertificateCreationFlow/util';
import CloningTray from '../../../../../../../../../../components/CloningTray';
import CloneProgressModal from '../../../../../../../../../../components/CloningTray/components/CloneProgressModal';
import { selectedIds } from '../../../../../../../../../../components/CloningTray/util';
import AllRequirements from '../../../../../../../../../../components/RequirementBlocks/AllRequirements';
import { analyticsIds } from '../../../../../../../../../../constants/analyticsIds';
import { CypressIds } from '../../../../../../../../../../constants/cypressIds';
import { FeatureTypes } from '../../../../../../../../../../constants/featureTypes';
import { inAppText } from '../../../../../../../../../../constants/inAppText';
import { Statuses } from '../../../../../../../../../../constants/statuses';
import { createErrorToast } from '../../../../../../../../../../redux/modules/app';
import { selectCompanyFeatures, selectCompanyId } from '../../../../../../../../../../redux/modules/company';
import { emailEditorReset, setInsuranceRequestInputs } from '../../../../../../../../../../redux/modules/emails';
import { hideModal, showModal } from '../../../../../../../../../../redux/modules/modal';
import { hideTray, showTray } from '../../../../../../../../../../redux/modules/tray';
import { selectUser } from '../../../../../../../../../../redux/modules/user';
import { insuredCertificateCreator } from '../../../../../../../../../../redux/reducer';
import { useAllCompanyPolicyTypesData, useCompanyData } from '../../../../../../../../../../services/company-service';
import { useInsuredData } from '../../../../../../../../../../services/insured-service';
import {
  useActiveRecordDocuments, useActiveRecordPolicies,
  useCreateRecordCloneBatch,
  useRecordData,
  useRecordDocumentRequirements,
  useRecordItemRequirements,
  useRecordLanguageRequirements,
  useRecordPolicyRequirements, useRecordQuestionRequirements
} from '../../../../../../../../../../services/record-service';
import CorrespondenceTray from '../../../../../../../trays/CorrespondenceTray';
import NotificationsTray from '../../../../../../../trays/NotificationsTray';
import RequestForInsuranceModal from '../RequestForInsuranceModal';

export const menuOptions = [
  {
    label: 'Email History',
    icon: BsInbox,
    id: 'emailHistory',
    dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.gearSettingsIcon.openEmailHistoryTray,
    handler: ({ e, dispatch, insuredId, recordId, jobId }) => {
      dispatch(showTray(() => (
        <CorrespondenceTray
          icon={BsInbox}
          insuredId={insuredId}
          jobId={jobId}
          recordId={recordId}
          title="Email History"
        />
      ), {
        size: '4xl',
        overlayClose: true,
        variant: 'secondary',
        onClose: () => dispatch(hideTray())
      }));
    }
  },
  {
    label: 'Notification History',
    icon: BsMailboxFlag,
    id: 'notificationHistory',
    handler: ({ e, dispatch, insuredId, recordId }) => {
      dispatch(showTray(() => (
        <NotificationsTray
          insuredId={insuredId}
          recordId={recordId}
        />
      ), {
        size: '4xl',
        overlayClose: true,
        variant: 'secondary',
        onClose: () => dispatch(hideTray())
      }));
    }
  },
  {
    label: 'Send Insurance Request',
    icon: BsEnvelope,
    id: 'sendEmail',
    dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.gearSettingsIcon.openSendEmail,
    cypressId: CypressIds.sendInsuranceRequestEmailButton,
    handler: ({ e, dispatch, insuredId, recordId, jobId }) => {
      dispatch(showModal(() => (
        <RequestForInsuranceModal insuredId={insuredId} jobId={jobId} recordId={recordId} />
      ), {
        center: true,
        size: '4xl',
        title: 'Send Request For Insurance',
        onClose: () => {
          dispatch(hideModal());

          dispatch(setInsuranceRequestInputs({
            to: null,
            cc: null
          }));

          dispatch(emailEditorReset());
        }
      }));
    }
  },
  {
    label: 'Update Requirements',
    icon: BsPencilSquare,
    id: 'updateRequirements',
    dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.gearSettingsIcon.openRequirementsEditor,
    cypressId: CypressIds.updateRequirementsButton,
    handler: async ({ e, dispatch, insuredId, history }) => {
      const requirementId = Number(e.target.value);

      const url = history.location.pathname.replace(
        /insureds.*/,
        `insureds/${insuredId}/update-requirements/${requirementId}`
      );
      history.push(url);
    }
  },
  {
    label: 'View Requirements',
    icon: BsClipboard,
    id: 'viewRequirements',
    dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.gearSettingsIcon.openProjectRequirements,
    handler: ({ e, dispatch, companyId, recordId }) => {
      dispatch(showTray(() => {
        const { data: policyRequirements } = useRecordPolicyRequirements(recordId);
        const { data: documentRequirements } = useRecordDocumentRequirements(recordId);
        const { data: itemRequirements } = useRecordItemRequirements(recordId);
        const { data: languageRequirements } = useRecordLanguageRequirements(recordId);
        const { data: policyTypes } = useAllCompanyPolicyTypesData(companyId);
        const { data: questionRequirements } = useRecordQuestionRequirements(recordId);

        const minimumRequirements = getMinimumInsuranceRequirements(
          itemRequirements,
          policyRequirements,
          policyTypes
        );

        return (
          <>
            <DrawerCloseButton />
            <DrawerBody>
              <AllRequirements
                documentRequirements={documentRequirements}
                languageRequirements={languageRequirements}
                minimumRequirements={minimumRequirements}
                questionRequirements={questionRequirements ?? []}
              />
            </DrawerBody>
          </>
        );
      }, {
        title: 'Insurance Requirements',
        size: '4xl',
        variant: 'secondary',
        onClose: () => dispatch(hideTray())
      }));
    }
  },
  {
    label: 'Create Certificate',
    icon: BsFilePlus,
    id: 'create',
    dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.gearSettingsIcon.openCreateCertificateTray,
    cypressId: CypressIds.ippCreateCertificateButton,
    handler: ({ e, dispatch, companyId, insuredId, recordId }) => {
      dispatch(showTray(() => {
        const { data: company } = useCompanyData(companyId);
        const { data: insured } = useInsuredData(insuredId);
        const { data: policyRequirements } = useRecordPolicyRequirements(recordId);
        const { data: itemRequirements } = useRecordItemRequirements(recordId);
        const { data: documentRequirements } = useRecordDocumentRequirements(recordId);
        const { data: languageRequirements } = useRecordLanguageRequirements(recordId);
        const { data: policyTypes } = useAllCompanyPolicyTypesData(companyId);
        const { data: record, isLoading: recordIsLoading } = useRecordData(recordId);

        const job = useMemo(() => {
          if (recordIsLoading) {
            return null;
          }

          return record?.job;
        }, [record]);

        const user = useSelector(selectUser);
        const { companyHasFeature: ocrEnabled } = useCompanyFeatures(FeatureFlags.OCR);
        const companyFeatures = useSelector(selectCompanyFeatures);

        const aiScanEnabled = useMemo(() => companyFeatures.find(f => [
          FeatureTypes.AiTierOne,
          FeatureTypes.AiTierTwo,
          FeatureTypes.AiTierThree
        ].includes(f.name))?.isEnabled, [companyFeatures]);

        return (
          <>
            <DrawerCloseButton />
            <CertificateCreationFlow
              blueprint={insuredCertificateCreator}
              company={company}
              documentRequirements={documentRequirements}
              insuranceRequirement={record}
              insured={insured}
              insuredRequirements={policyRequirements}
              itemRequirements={itemRequirements}
              job={job}
              languageRequirements={languageRequirements}
              options={{
                ocr: true,
                shouldSync: true,
                aiScan: aiScanEnabled
              }}
              policyReviewContentHeight="75vh"
              policyTypes={policyTypes}
              user={user}
            />
          </>

        );
      }, {
        size: '4xl',
        variant: 'secondary',
        onClose: () => dispatch(hideTray())
      }));
    }
  },
  {
    label: 'Clone Insurance',
    icon: BsSubtract,
    id: 'clone',
    dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.gearSettingsIcon.openCloneInsuranceModal,
    handler: ({ e, dispatch, recordId, insuredId }) => {
      dispatch(showTray(() => {
        const { data: policies, isLoading: policiesLoading } = useActiveRecordPolicies(recordId);
        const { data: documents, isLoading: documentsLoading } = useActiveRecordDocuments({ recordId });
        const { data: record, isLoading: recordLoading } = useRecordData(recordId);
        const { data: insured } = useInsuredData(insuredId);

        const companyId = useSelector(selectCompanyId);
        const { mutate: createCloneBatch, isLoading: isCloning } = useCreateRecordCloneBatch({
          onSuccess: (batch, { recordIds }) => {
            dispatch(hideTray());
            dispatch(showModal(() => (
              <CloneProgressModal batchId={batch.id} insuredId={insuredId} recordIds={recordIds} />
            ), {
              actionRequired: true,
              buttons: {},
              size: 'md',
              title: 'Cloning Insurance',
              onClose: () => {}
            }));
          },
          onError: error => dispatch(createErrorToast({
            title: 'Uh oh.',
            description: 'Something went wrong starting the cloning process.',
            error
          }))
        });

        const activePolicies = useMemo(() => (policiesLoading ? [] : policies), [policies, policiesLoading]);
        const activeDocuments = useMemo(() => (documentsLoading ? [] : documents), [documents, documentsLoading]);

        const currentJob = useMemo(() => {
          if (recordLoading) {
            return null;
          }

          return record?.job;
        }, [record]);

        return (
          <>
            <DrawerCloseButton />
            <CloningTray
              companyId={companyId}
              currentRecord={record}
              documents={activeDocuments}
              handleExit={() => dispatch(hideTray())}
              handleSubmit={(recordObject, policyObject, documentObject, { copyRequirementStatuses = false } = {}) => {
                const policyIds = selectedIds(policyObject);
                const documentIds = selectedIds(documentObject);
                const recordIds = selectedIds(recordObject);

                if (!policyIds.length && !documentIds.length) {
                  dispatch(createErrorToast({
                    title: 'No policies or documents selected.',
                    description: 'Please select at least one policy or document to clone.'
                  }));

                  return;
                }

                if (!recordIds.length) {
                  dispatch(createErrorToast({
                    title: 'No records selected.',
                    description: 'Please select at least one record.'
                  }));

                  return;
                }

                createCloneBatch({ recordId, recordIds, policyIds, documentIds, copyRequirementStatuses });
              }}
              insuredId={insuredId}
              insuredName={insured?.insuredCompanyName}
              isLoading={isCloning}
              job={currentJob}
              policies={activePolicies}
            />
          </>

        );
      }, {
        size: 'lg',
        variant: 'secondary',
        onClose: () => dispatch(hideTray())
      }));
    }
  }
];
